import { DeepLinkSourceSchema, type DeepLinkSource } from '../schemas/shop.schema';

/**
 * Carry the deep-link `?src=` from the page that OBSERVES it to the call that can
 * REPORT it.
 *
 * ── WHY THIS EXISTS AT ALL ─────────────────────────────────────────────────
 * `?src=ptc` has driven storefront behaviour since E-SF02, but the two events are
 * on different pages:
 *
 *   observed   `/plan/:slug?src=ptc`    — a Power to Choose deep link; the shopper
 *                                         has no funnel session yet
 *   reportable `GET /bff/session`       — fired by the meter chooser / sub-flows,
 *                                         the only calls that legitimately mint one
 *
 * In between, the shopper navigates through links built by `buildPlanStepPath`,
 * which do not carry `src` — so without somewhere to put it, the attribution is
 * gone by the time there is anything to attach it to. That is exactly why the BFF
 * used to hardcode `source: 'organic'` on the Accounts prospect.
 *
 * ── WHY NOT SIMPLY MINT THE SESSION ON THE DEEP-LINK PAGE ──────────────────
 * Because that is a documented architectural rule, not an oversight:
 * `FunnelHydrationGate` states that only the meter sub-flows may mint, since
 * minting on a landing would turn every deep link into an anonymous entry point —
 * and the mint is IP-capped at 10/hour, a budget that must not be spent on people
 * merely LOOKING at plans. So the value waits here for a call that was going to
 * happen anyway.
 *
 * ── WHY NOT `FunnelProvider` ───────────────────────────────────────────────
 * The funnel context is in-memory and, by its own header, resets on reload. A
 * shopper who refreshes between the deep link and the meter step would be silently
 * re-attributed to organic — the precise failure this is fixing. `sessionStorage`
 * is per-tab and survives a refresh, which is the lifetime of one shopping visit.
 *
 * ── TRUST POSTURE ──────────────────────────────────────────────────────────
 * ⚠️ Nothing here is a control. The value originates in a URL, so it is
 * attacker-controlled, and `sessionStorage` is writable by anyone with a console.
 * The AUTHORITATIVE validation is server-side (`funnel/attribution-session.ts`
 * parses into a closed enum before it reaches Accounts). Parsing on read here is
 * defence in depth and keeps the type honest — it is not what makes this safe.
 */

/** Per-tab key. Namespaced so it cannot collide with anything else on the origin. */
const STORAGE_KEY = 'rheora.shop.src';

/**
 * `sessionStorage` throws in Safari private mode and is absent in non-browser
 * renders (tests, SSR). Attribution is a nice-to-have: it must never be able to
 * break the storefront, so every access is total.
 */
function storage(): Storage | null {
  try {
    return typeof globalThis.sessionStorage === 'undefined' ? null : globalThis.sessionStorage;
  } catch {
    return null;
  }
}

/**
 * Record the observed source for this visit.
 *
 * MIRRORS THE SERVER RULE — first non-organic touch wins:
 *   - `organic` never overwrites a recorded source (a later page without `?src`
 *     must not re-attribute a PTC shopper); and
 *   - a recorded source is never replaced (attribution is a claim about where the
 *     visit CAME FROM, so the first one is the true one).
 *
 * Keeping the two rules identical means the client and the BFF cannot disagree
 * about which of two observations won.
 */
export function rememberSource(source: DeepLinkSource): void {
  if (source === 'organic') return;
  const store = storage();
  if (store === null) return;
  try {
    if (store.getItem(STORAGE_KEY) !== null) return;
    store.setItem(STORAGE_KEY, source);
  } catch {
    // Quota or a disabled store. Losing the attribution is acceptable; throwing
    // out of a render path is not.
  }
}

/**
 * The source to report on the next session-establishing call.
 *
 * @returns the remembered source, or `'organic'` when absent/unreadable/tampered.
 */
export function recallSource(): DeepLinkSource {
  const store = storage();
  if (store === null) return 'organic';
  let raw: string | null;
  try {
    raw = store.getItem(STORAGE_KEY);
  } catch {
    return 'organic';
  }
  const parsed = DeepLinkSourceSchema.safeParse(raw);
  return parsed.success ? parsed.data : 'organic';
}

/** Drop the remembered source. Test seam; not used by the funnel. */
export function forgetSource(): void {
  const store = storage();
  if (store === null) return;
  try {
    store.removeItem(STORAGE_KEY);
  } catch {
    // Same posture as `rememberSource`.
  }
}
